<div class="sso-connection">
  @if (loading) {
    <div class="spinner-border text-secondary" role="status">
      <span class="visually-hidden">Loading</span>
    </div>
  } @else if (loadError || !connection) {
    <a class="sso-connection__back" routerLink="/admin/admin/sso-connections">
      <i class="bi bi-arrow-left me-1" aria-hidden="true"></i>SSO connections
    </a>
    <div class="alert alert-danger" role="alert">{{ loadError }}</div>
  } @else {
    <header class="page-head">
      <div>
        <a
          class="sso-connection__back"
          routerLink="/admin/admin/sso-connections"
        >
          <i class="bi bi-arrow-left me-1" aria-hidden="true"></i>SSO
          connections
        </a>
        <div class="sso-connection__title">
          <h1>{{ connection.name }}</h1>
          <span [class]="'chip ' + statusChips[connection.status].className">{{
            statusChips[connection.status].label
          }}</span>
        </div>
        <p class="lede">
          {{ protocolLabels[connection.protocol] }} · {{ forLabel }}
        </p>
      </div>
      <div class="actions">
        @if (connection.status === 'active') {
          <button
            type="button"
            class="btn btn-outline"
            [disabled]="changingStatus"
            (click)="disable()"
          >
            Disable
          </button>
        } @else {
          <!-- A disabled button fires no mouse events, so the tooltip sits on a wrapper. -->
          <span
            [title]="
              canActivate ? '' : 'Add a domain and a role mapping first.'
            "
          >
            <button
              type="button"
              class="btn btn-primary"
              [disabled]="changingStatus || !canActivate"
              (click)="activate()"
            >
              Activate
            </button>
          </span>
        }
      </div>
    </header>

    @if (statusError) {
      <div class="alert alert-danger" role="alert">{{ statusError }}</div>
    }

    <div class="sso-connection__layout">
      <div class="sso-connection__column">
        <section class="sso-card" aria-labelledby="sso-card-connection">
          <p class="sso-card__eyebrow" id="sso-card-connection">Connection</p>
          <div class="sso-card__fields">
            <div class="sso-card__field--wide">
              <label class="form-label" for="sso-name">Name</label>
              <input
                id="sso-name"
                class="form-control"
                maxlength="128"
                [(ngModel)]="nameDraft"
              />
            </div>
            <div>
              <label class="form-label" for="sso-protocol">Protocol</label>
              <input
                id="sso-protocol"
                class="form-control"
                [value]="protocolLabels[connection.protocol]"
                readonly
              />
            </div>
            <div>
              <label class="form-label" for="sso-for">For</label>
              <input
                id="sso-for"
                class="form-control"
                [value]="
                  connection.scope === 'client'
                    ? 'Client: ' + forLabel
                    : forLabel
                "
                readonly
              />
            </div>
          </div>
          <p class="sso-card__hint">
            Protocol and client can't change after a connection is created.
          </p>
          @if (supportsRequireSso) {
            <div class="form-check mt-3">
              <input
                id="sso-require-sso"
                type="checkbox"
                class="form-check-input"
                aria-describedby="sso-require-sso-hint"
                [(ngModel)]="requireSsoDraft"
              />
              <label class="form-check-label" for="sso-require-sso">
                Require SSO sign-in
              </label>
            </div>
            <p id="sso-require-sso-hint" class="sso-card__hint">
              {{ requireSsoHint }}
            </p>
          }
          @if (cardErrors.connection) {
            <div class="alert alert-danger mb-0 mt-3" role="alert">
              {{ cardErrors.connection }}
            </div>
          }
          <div class="sso-card__foot">
            <button
              type="button"
              class="btn btn-primary btn-sm"
              [disabled]="!canSave('connection')"
              (click)="saveSettings('connection')"
            >
              Save
            </button>
          </div>
        </section>

        <section class="sso-card" aria-labelledby="sso-card-provider">
          <p class="sso-card__eyebrow" id="sso-card-provider">
            {{ providerEyebrow }}
          </p>
          @if (connection.protocol === 'oidc') {
            <div class="sso-card__fields">
              <div class="sso-card__field--wide">
                <label class="form-label" for="sso-oidc-issuer"
                  >Issuer URL</label
                >
                <input
                  id="sso-oidc-issuer"
                  class="form-control"
                  maxlength="255"
                  [(ngModel)]="provider.oidcIssuerUrl"
                />
              </div>
              <div class="sso-card__field--wide">
                <label class="form-label" for="sso-oidc-client"
                  >Client ID</label
                >
                <input
                  id="sso-oidc-client"
                  class="form-control"
                  maxlength="255"
                  [(ngModel)]="provider.oidcClientId"
                />
              </div>
              <div class="sso-card__field--wide">
                <label class="form-label" for="sso-oidc-scopes">Scopes</label>
                <input
                  id="sso-oidc-scopes"
                  class="form-control"
                  maxlength="255"
                  [(ngModel)]="provider.oidcScopes"
                />
                <p class="sso-card__hint">Separated by spaces.</p>
              </div>
            </div>
          } @else {
            <div class="sso-card__fields">
              <div class="sso-card__field--wide">
                <label class="form-label" for="sso-saml-entity"
                  >IdP entity ID</label
                >
                <input
                  id="sso-saml-entity"
                  class="form-control"
                  maxlength="255"
                  [(ngModel)]="provider.samlIdpEntityId"
                />
              </div>
              <div class="sso-card__field--wide">
                <label class="form-label" for="sso-saml-url"
                  >IdP sign-in URL</label
                >
                <input
                  id="sso-saml-url"
                  class="form-control"
                  maxlength="255"
                  [(ngModel)]="provider.samlIdpSsoUrl"
                />
              </div>
              <div class="sso-card__field--wide">
                <label class="form-label" for="sso-saml-cert-0"
                  >Signing certificates (up to 5)</label
                >
                @for (
                  certificate of provider.samlIdpCerts;
                  track $index;
                  let i = $index
                ) {
                  <div class="sso-connection__certificate">
                    <textarea
                      class="form-control"
                      rows="5"
                      [id]="'sso-saml-cert-' + i"
                      [(ngModel)]="provider.samlIdpCerts[i]"
                    ></textarea>
                    @if (provider.samlIdpCerts.length > 1) {
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm"
                        (click)="removeCertificate(i)"
                      >
                        Remove
                      </button>
                    }
                  </div>
                }
                <p class="sso-card__hint">
                  Add the new certificate before the client rotates, then remove
                  the old one.
                </p>
                @if (provider.samlIdpCerts.length < maxCertificates) {
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm px-0"
                    (click)="addCertificate()"
                  >
                    <i class="bi bi-plus-lg me-1" aria-hidden="true"></i>Add
                    certificate
                  </button>
                }
              </div>
              <div class="sso-card__field--wide form-check">
                <input
                  id="sso-saml-signed"
                  type="checkbox"
                  class="form-check-input"
                  [(ngModel)]="provider.samlRequireSignedResponse"
                />
                <label class="form-check-label" for="sso-saml-signed">
                  Require the whole response to be signed
                </label>
              </div>
            </div>
          }
          @if (cardErrors.provider) {
            <div class="alert alert-danger mb-0 mt-3" role="alert">
              {{ cardErrors.provider }}
            </div>
          }
          <div class="sso-card__foot">
            <button
              type="button"
              class="btn btn-primary btn-sm"
              [disabled]="!canSave('provider')"
              (click)="saveSettings('provider')"
            >
              Save
            </button>
          </div>
        </section>

        <section class="sso-card" aria-labelledby="sso-card-attributes">
          <p class="sso-card__eyebrow" id="sso-card-attributes">Attributes</p>
          <div class="sso-card__fields">
            <div>
              <label class="form-label" for="sso-attr-groups">Groups</label>
              <input
                id="sso-attr-groups"
                class="form-control"
                maxlength="64"
                [(ngModel)]="attributes.groupsClaim"
              />
            </div>
            @if (connection.protocol === 'saml') {
              <div>
                <label class="form-label" for="sso-attr-email">Email</label>
                <input
                  id="sso-attr-email"
                  class="form-control"
                  maxlength="64"
                  [(ngModel)]="attributes.emailAttribute"
                />
                <p class="sso-card__hint">
                  Leave blank to use the NameID as the email.
                </p>
              </div>
            }
            <div>
              <label class="form-label" for="sso-attr-first">First name</label>
              <input
                id="sso-attr-first"
                class="form-control"
                maxlength="64"
                [(ngModel)]="attributes.firstNameAttribute"
              />
            </div>
            <div>
              <label class="form-label" for="sso-attr-last">Last name</label>
              <input
                id="sso-attr-last"
                class="form-control"
                maxlength="64"
                [(ngModel)]="attributes.lastNameAttribute"
              />
            </div>
          </div>
          @if (cardErrors.attributes) {
            <div class="alert alert-danger mb-0 mt-3" role="alert">
              {{ cardErrors.attributes }}
            </div>
          }
          <div class="sso-card__foot">
            <button
              type="button"
              class="btn btn-primary btn-sm"
              [disabled]="!canSave('attributes')"
              (click)="saveSettings('attributes')"
            >
              Save
            </button>
          </div>
        </section>

        <app-sso-connection-domains-card
          [connection]="connection"
          (saved)="onDomainsSaved($event)"
        ></app-sso-connection-domains-card>

        <app-sso-connection-role-mappings-card
          [connection]="connection"
          (saved)="onRoleMappingsSaved($event)"
        ></app-sso-connection-role-mappings-card>
      </div>

      <aside class="sso-connection__column">
        <section class="sso-card" aria-labelledby="sso-card-ours">
          <p class="sso-card__eyebrow" id="sso-card-ours">
            {{ ourSideEyebrow }}
          </p>
          <dl class="sso-connection__urls">
            @for (row of ourSide; track row.label) {
              <dt>{{ row.label }}</dt>
              <dd>
                <code>{{ row.url }}</code>
                <button
                  type="button"
                  class="btn btn-ghost btn-sm"
                  (click)="copy(row)"
                >
                  {{ copiedLabel === row.label ? 'Copied' : 'Copy' }}
                </button>
              </dd>
            }
          </dl>
          @if (connection.protocol === 'oidc') {
            <p class="sso-card__hint mb-0">
              VIVE signs in with a private key, so there is no client secret to
              share.
            </p>
          }
        </section>

        @if (connection.status !== 'active') {
          <section class="sso-card" aria-labelledby="sso-card-before">
            <p class="sso-card__eyebrow" id="sso-card-before">
              Before you activate
            </p>
            <p class="sso-card__hint mb-0">
              Activate checks the identity provider fields only. Add at least
              one domain and one role mapping first, or nobody can sign in
              through this connection.
            </p>
          </section>
        }
      </aside>
    </div>
  }
</div>
